/**
 * IndianFamilyTreeHome — landing page for indianfamilytree.com.
 *
 * Design pillar: a *historic* aesthetic — aged parchment paper, sepia
 * palm-leaf motifs, oxidised gold accents. Every section feels like a
 * chapter of a bound family archive rather than a modern SaaS
 * marketing page.
 *
 * Preview modes:
 *   /?site=indianfamilytree     → swapped in via HomeRouter
 *   https://indianfamilytree.com → auto-detected via hostname
 *
 * All data goes through the shared Social Pulse API using
 * `X-Site-Source: indianfamilytree` — no IFT-only backend routes.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  TreePine, Users, ArrowRight, ScrollText, BookOpen, MapPin, Feather,
  Flame, Compass, Landmark, Milestone, Archive, Search, Sparkles,
} from "lucide-react";

import SeoHead from "@/components/SeoHead";
import IftIntroArticle from "@/components/IftIntroArticle";
import HeritageResourcesSection from "@/components/HeritageResourcesSection";
import CousinDiscoveryFeed from "@/components/CousinDiscoveryFeed";

const API = process.env.REACT_APP_BACKEND_URL;

// Historic palette — aged parchment + sepia + oxidised gold + tulsi green.
// Kept local so tweaks here never affect the parent design tokens.
const P = {
  bg:         "#F5EAD3",   // parchment
  bgDeep:     "#E8D9B5",   // aged band
  ink:        "#3A1F0F",   // heirloom brown
  inkSoft:    "#6B4A2E",
  saffron:    "#B25D38",   // warm terracotta
  saffronDeep:"#8A3F22",
  gold:       "#C08A2E",   // oxidised gold
  goldSoft:   "#EFDDB0",
  tulsi:      "#0E7C55",   // deep tulsi green
  ivory:      "#FFF8E6",
};

// Scrolling "family" ticker — exact captions requested by the brand.
// Displayed as a slim marquee at the very top of every IFT page.
const LANG_TICKER = [
  { word: "परिवार",    lang: "Hindi"      },
  { word: "Vamsham",   lang: "Malayalam"  },
  { word: "வம்சம்",    lang: "Tamil"      },
  { word: "कुटुम्ब",    lang: "Sanskrit"   },
  { word: "Family",    lang: "English"    },
  { word: "പരിവാര്‍",  lang: "Malayalam"  },
  { word: "കുടുംബം",   lang: "Malayalam"  },
  { word: "Kula",      lang: "Sanskrit"   },
  // Bonus richness across other Indian tongues.
  { word: "Kutumb",    lang: "Hindi"      },
  { word: "Parivāra",  lang: "Kannada"    },
  { word: "ਪਰਿਵਾਰ",    lang: "Punjabi"    },
  { word: "পরিবার",    lang: "Bengali"    },
  { word: "કુટુંબ",    lang: "Gujarati"   },
  { word: "కుటుంబం",   lang: "Telugu"     },
];

// Six-chapter manifesto — the north-star features of the platform.
const CHAPTERS = [
  { i: "I",   title: "Family Tree Builder",  Icon: TreePine,
    body:  "Ancestor-first canvas designed for multi-generational Indian families. Add spouses, siblings, cousins, in-laws — with gotra, star, community tags baked in." },
  { i: "II",  title: "Memory Vault",         Icon: Archive,
    body:  "Photographs, letters, audio recordings, oral histories — every artefact tagged, dated, safely archived and shared with future generations." },
  { i: "III", title: "Heritage Maps",        Icon: Compass,
    body:  "Trace ancestral villages across India. Overlay migration routes, marriage alliances, and diaspora branches on the same living map." },
  { i: "IV",  title: "Surnames & Gotras",    Icon: ScrollText,
    body:  "Explore surname etymologies, gotra lineages, kula deivams and community records. A quiet library of who we are and where we came from." },
  { i: "V",   title: "Diaspora Timelines",   Icon: Milestone,
    body:  "Watch a family's story move from a Kerala village to Chennai to the Gulf to North America — laid out on a single generational timeline." },
  { i: "VI",  title: "Social Pulse Network", Icon: Sparkles,
    body:  "Cousin discovery across the whole Social Pulse network. Your family's stories intersect with millions of others — beautifully, respectfully." },
];

// Featured collections — content hubs at the bottom of the page.
const COLLECTIONS = [
  { slug: "freedom-fighters",     name: "Freedom Fighters",       hint: "Families who shaped 1857–1947",  Icon: Flame },
  { slug: "village-archives",     name: "Village Archives",       hint: "Panchayat records, land deeds",  Icon: Landmark },
  { slug: "diaspora-stories",     name: "Diaspora Stories",       hint: "Journeys that crossed oceans",   Icon: Compass },
  { slug: "historic-homes",       name: "Historic Homes",         hint: "The ancestral house, remembered", Icon: BookOpen },
  { slug: "traditional-occupations", name: "Traditional Occupations", hint: "Weavers, priests, seafarers", Icon: Feather },
];

// Migration timeline — one canonical journey retold to illustrate.
const MIGRATION_STOPS = [
  { where: "Ancestral village",    when: "1830s", note: "Rice paddies, tharavadu house, jyotisham records" },
  { where: "Provincial town",      when: "1890s", note: "Colonial administration, mission schools" },
  { where: "Bombay / Madras",      when: "1940s", note: "Independence, first offices, first cameras" },
  { where: "London / Manchester",  when: "1960s", note: "NHS, engineering, first family passports" },
  { where: "US · Canada · Gulf",   when: "1980s", note: "Tech, medicine, remittance economy" },
  { where: "Australia · Singapore", when: "2000s", note: "Second-gen networks, cousins in seven cities" },
];

// Real-world diaspora corridors that show up again and again in the
// archive — displayed as a second panel next to the canonical journey.
const MIGRATION_ROUTES = [
  { from: "Palakkad",  to: "Bombay",       year: "1956", region: "Village → City" },
  { from: "Punjab",    to: "Southall",     year: "1967", region: "India → United Kingdom" },
  { from: "Gujarat",   to: "Nairobi",      year: "1898", region: "India → East Africa" },
  { from: "Andhra",    to: "New Jersey",   year: "1985", region: "India → United States" },
  { from: "Punjab",    to: "Brampton",     year: "1974", region: "India → Canada" },
  { from: "Kerala",    to: "Melbourne",    year: "2003", region: "India → Australia" },
];

// Common historical-record types the archive helps you search.
const RECORD_TYPES = [
  "Census records","Land records","Temple records","Church records",
  "Mosque records","Birth records","Marriage records","Death records",
  "School records","Military records","Court records","Newspapers",
  "Gazetteers","Passenger manifests",
];

// Curated (real) featured trees. The hero cards prioritise the two
// hand-crafted family trees you already have data for; the remaining
// three are placeholders that gracefully link to /family-trees.
const FEATURED_TREES = [
  { slug: "vellaringattu-family",  name: "Vellaringattu Family",   region: "Kerala",     motif: "❦",
    tagline: "Seven generations from the Meenachil river valley." },
  { slug: "kurisummoottil-family", name: "Kurisummoottil Family",  region: "Kerala",     motif: "⁂",
    tagline: "A branching lineage rooted in Kanjirappally and its diaspora." },
  { slug: null, name: "Nair · Malabar",       region: "Kozhikode",     motif: "❦", tagline: "Matrilineal tharavadus of the Malabar coast." },
  { slug: null, name: "Iyer · Palakkad",      region: "Palakkad",      motif: "⁂", tagline: "Migrated Tamil Brahmins along the Bharathapuzha." },
  { slug: null, name: "Patel · Kutch",        region: "Gujarat",       motif: "❦", tagline: "Traders and diamond-cutters of the west coast." },
  { slug: null, name: "Singh · Doaba",        region: "Punjab",        motif: "⁂", tagline: "Farming lineages between the Beas and the Sutlej." },
];

// Real memorials curated for the "Celebrate a Life" section. Each links
// to `/celebrate/:slug`, which already exists in the app.
const MEMORIALS = [
  { slug: "mahatma-gandhi-1869-1948",     name: "Mahatma Gandhi",       years: "1869–1948", note: "Father of the nation" },
  { slug: "mother-teresa-1910-1997",      name: "Mother Teresa",        years: "1910–1997", note: "Missionary of Charity" },
  { slug: "col-santosh-babu-galwan-hero", name: "Col. Santosh Babu",    years: "1983–2020", note: "Galwan Valley, Ladakh" },
  { slug: "dr-anjali-rao-1954-2024",      name: "Dr. Anjali Rao",       years: "1954–2024", note: "Community physician, Bengaluru" },
];

const INDIAN_STATES = [
  "Kerala","Tamil Nadu","Karnataka","Andhra Pradesh","Telangana",
  "Maharashtra","Goa","Gujarat","Rajasthan","Punjab","Haryana",
  "Delhi","Uttar Pradesh","West Bengal","Odisha","Bihar","Jharkhand",
  "Assam","Meghalaya","Manipur","Nagaland","Sikkim","Chhattisgarh",
  "Madhya Pradesh","Himachal Pradesh","Uttarakhand","Puducherry","Ladakh",
];


// ── Reusable historic building blocks ──────────────────────────────

function Ornament({ char = "❦" }) {
  return (
    <div className="flex items-center gap-3 my-6" aria-hidden>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.gold}55, ${P.gold}, ${P.gold}55, transparent)` }} />
      <span className="text-lg" style={{ color: P.gold }}>{char}</span>
      <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${P.gold}55, ${P.gold}, ${P.gold}55, transparent)` }} />
    </div>
  );
}

function Kicker({ children, roman }) {
  return (
    <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.3em]" style={{ color: P.gold }}>
      {roman && <span className="font-display italic text-base" style={{ color: P.saffron }}>{roman}</span>}
      <span>{children}</span>
    </div>
  );
}


// ── Section: Historic hero ────────────────────────────────────────

// New IFT logo — hosted asset. If it fails to load we quietly fall back
// to the historical TreePine glyph so the hero never renders blank.
const IFT_LOGO_URL = "/ift/logo-tall.png";
const IFT_LOGO_MARK_URL = "/ift/logo-mark.png";

// Four canonical taglines under the logo (verbatim from the brand).
const IFT_TAGLINES = [
  "Indians, Discover Your Roots.",
  "Celebrate Your Heritage.",
  "Preserve Your Legacy.",
];
const IFT_SUBTAG = "A Living Archive for Every Indian Family and Home";

// Four brand phrases for the top tagline strip (saffron bar). Each
// phrase now deep-links to its matching chapter on the /story article
// via URL anchor.
const IFT_STRIP_PHRASES = [
  { label: "Discover Your Roots",       anchor: "discover-your-roots" },
  { label: "Celebrate Your Heritage",   anchor: "celebrate-your-heritage" },
  { label: "Preserve Your Legacy",      anchor: "preserve-your-legacy" },
  { label: "Celebrate Every Indian Life", anchor: "celebrate-every-indian-life" },
];

// A public Rajasthani / Mughal arch photograph. Uses Pexels CDN
// (verified reachable from this pod — Wikimedia is currently rejecting
// our UA with 400). Photo credit stays with the original creator.
const IFT_HERO_ARCH_URL =
  "https://images.pexels.com/photos/3581916/pexels-photo-3581916.jpeg?auto=compress&cs=tinysrgb&w=800&h=1000&fit=crop";

// Elderly grandmother portrait — Pexels free-license stock.
const IFT_HERO_PORTRAIT_URL =
  "https://images.pexels.com/photos/1642883/pexels-photo-1642883.jpeg?auto=compress&cs=tinysrgb&w=200&h=200&fit=crop";

// ── Section: Saffron tagline strip ────────────────────────────────
// Slim horizontal bar under (or above) the main nav bar. Saffron
// background, black caps letters, four brand phrases separated by
// diamond dividers. Scrolls slowly on mobile (marquee) so all four
// remain legible on narrow screens.
function TaglineStrip() {
  return (
    <div
      className="w-full border-y"
      style={{
        background: "#FF9933",           // Indian flag saffron
        borderColor: "rgba(0,0,0,0.15)",
        color: "#1A0F00",
      }}
      data-testid="ift-tagline-strip"
    >
      <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-4 flex-wrap text-center">
        {IFT_STRIP_PHRASES.map((phrase, i) => (
          <span
            key={phrase.anchor}
            data-testid={`ift-strip-phrase-${i}`}
            className="inline-flex items-center gap-4 font-semibold uppercase tracking-[0.18em] text-[11px] sm:text-[12.5px] whitespace-nowrap"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            <Link
              to={`/indian-family-tree/story#${phrase.anchor}`}
              className="hover:underline focus:outline-none focus:underline transition-opacity hover:opacity-80"
              data-testid={`ift-strip-link-${phrase.anchor}`}
            >
              {phrase.label}
            </Link>
            {i < IFT_STRIP_PHRASES.length - 1 && (
              <span aria-hidden style={{ color: "#8A3F22" }}>◆</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}

function Hero() {
  return (
    <section
      className="relative overflow-hidden"
      style={{
        background: `linear-gradient(180deg, ${P.bgDeep} 0%, ${P.bg} 60%)`,
      }}
      data-testid="ift-hero"
    >
      {/* subtle paper-noise texture via SVG */}
      <svg className="absolute inset-0 w-full h-full opacity-[0.06] pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        <filter id="ift-hero-noise"><feTurbulence type="fractalNoise" baseFrequency="0.9" numOctaves="2" /></filter>
        <rect width="100%" height="100%" filter="url(#ift-hero-noise)" />
      </svg>

      <div className="relative max-w-7xl mx-auto px-4 lg:px-8 py-12 lg:py-20 grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-center">
        {/* ─── Left column: heading + copy + CTAs + search ───── */}
        <div>
          <h1
            className="font-display leading-[0.98] tracking-tight text-5xl sm:text-6xl lg:text-7xl"
            style={{ color: P.ink }}
            data-testid="ift-hero-title"
          >
            Celebrate Every
            <br />
            <span
              className="italic"
              style={{ color: P.saffronDeep, fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Indian Life.
            </span>
          </h1>

          <p
            className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed"
            style={{ color: P.inkSoft }}
            data-testid="ift-hero-subhead"
          >
            Create a lasting memorial, build your family tree across every state of
            India, and discover global lineages. Start preserving your family history
            today &mdash; a quiet archive your grandchildren will one day thank you for.
          </p>

          <div className="mt-8 flex flex-wrap gap-3" data-testid="ift-hero-ctas">
            <Link
              to="/family-trees/new"
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition"
              style={{ background: P.saffron }}
              data-testid="ift-hero-start-tree"
            >
              <TreePine className="w-4 h-4" /> Start My Family Tree
            </Link>
            <Link
              to="/family-trees"
              className="inline-flex items-center gap-2 rounded-full px-5 py-3 font-semibold border-2 hover:shadow-md transition"
              style={{ borderColor: P.ink, color: P.ink, background: `${P.ivory}99` }}
              data-testid="ift-hero-browse-trees"
            >
              <Users className="w-4 h-4" /> Browse All Trees <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link
              to="/celebrate"
              className="inline-flex flex-col items-center gap-0 rounded-full px-5 py-2 font-semibold text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition"
              style={{ background: P.ink }}
              data-testid="ift-hero-celebrate"
            >
              <span className="inline-flex items-center gap-2"><Flame className="w-4 h-4" /> Celebrate a Life</span>
              <span className="text-[9px] font-mono uppercase tracking-widest opacity-80">(Memorials)</span>
            </Link>
          </div>

          {/* Sample-tree strip — same saffron CTA as /family-trees/new
              so brand-new visitors landing on the homepage can preview
              a fully-populated tree before signing up. */}
          <Link
            to="/family/sample-heritage-family"
            data-testid="ift-hero-sample-tree"
            className="mt-6 block rounded-2xl border overflow-hidden group hover:shadow-lg transition-shadow max-w-xl"
            style={{ borderColor: "#E8DFCF", background: "linear-gradient(135deg, #FFF3D6 0%, #F6C583 60%, #C2410C 130%)" }}
          >
            <div className="flex items-center gap-4 px-4 sm:px-5 py-4" style={{ color: P.ink }}>
              <div className="w-11 h-11 rounded-full bg-white/70 flex items-center justify-center flex-shrink-0 shadow-sm">
                <Sparkles className="w-5 h-5" style={{ color: P.saffronDeep }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-80">Sample tree — a fictional family</div>
                <div className="text-base font-semibold">Explore The Sample Heritage Family</div>
                <div className="text-xs opacity-80 mt-0.5">5 generations · 17 members · 2 memorials · family message boards</div>
              </div>
              <ArrowRight className="w-5 h-5 opacity-70 group-hover:translate-x-0.5 transition-transform" />
            </div>
            <img
              src="/ift/sample-tree-illustration.jpg"
              alt="How your Indian Family Tree looks — colour-coded generations with tree and profile views"
              className="w-full object-cover border-t"
              style={{ borderColor: "#E8DFCF", maxHeight: 260, objectPosition: "top" }}
              loading="lazy"
              data-testid="ift-hero-tree-illustration"
            />
          </Link>

          {/* Premium banner showcase */}
          <div className="mt-6 max-w-xl" data-testid="ift-banner-showcase">
            <div className="text-[10px] font-mono uppercase tracking-widest mb-2" style={{ color: P.inkSoft }}>
              Premium · Custom family banners
            </div>
            <Link to="/pricing" className="grid grid-cols-3 gap-1.5 group" data-testid="ift-banner-showcase-link">
              {[
                "https://customer-assets-lxgj4vgw.emergentagent.net/job_5722e3b3-fe12-4935-94b5-aee51b23f920/artifacts/qasknzal_nair-family-graphics.webp",
                "https://customer-assets-lxgj4vgw.emergentagent.net/job_5722e3b3-fe12-4935-94b5-aee51b23f920/artifacts/cqoc542c_indin-sikh-family6.webp",
                "https://customer-assets-lxgj4vgw.emergentagent.net/job_5722e3b3-fe12-4935-94b5-aee51b23f920/artifacts/fk60gvwf_vellaringattu-family-graphics.webp",
              ].map((src, i) => (
                <img key={i} src={src} alt="Sample family banner design" loading="lazy"
                  className="w-full rounded-lg object-cover shadow-sm group-hover:shadow-md transition"
                  style={{ aspectRatio: "3.2/1", border: `1px solid ${P.gold}44` }} />
              ))}
            </Link>
            <p className="text-xs mt-1.5" style={{ color: P.inkSoft }}>
              AI-designed heritage banners with your family name — included with every paid plan.{" "}
              <Link to="/pricing" className="underline font-semibold" style={{ color: P.saffron }}>See plans →</Link>
            </p>
          </div>

          {/* Ancestor search box */}
          <form
            action="/family-trees/ancestor-search"
            method="get"
            className="mt-6 flex items-center gap-2 rounded-full pl-4 pr-1.5 py-1.5 shadow-md max-w-xl"
            style={{ background: P.ivory, border: `1px solid ${P.gold}55` }}
            data-testid="ift-hero-search"
          >
            <Search className="w-4 h-4 flex-none" style={{ color: P.saffron }} />
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>Find an ancestor</div>
              <input
                type="search"
                name="q"
                placeholder="Search every public tree"
                className="w-full bg-transparent text-sm outline-none"
                style={{ color: P.ink }}
                data-testid="ift-hero-search-input"
                aria-label="Find an ancestor — search every public tree"
              />
            </div>
            <button
              type="submit"
              className="rounded-full px-5 py-2 text-sm font-semibold text-white shadow"
              style={{ background: P.saffron }}
              data-testid="ift-hero-search-submit"
            >
              Search
            </button>
          </form>

          {/* Language ticker chip — surfaces "family" in every Indian tongue */}
          <div className="mt-6 flex items-center gap-2 flex-wrap text-xs" data-testid="ift-hero-lang-chips">
            <span className="font-mono uppercase tracking-widest text-[10px]" style={{ color: P.gold }}>Family, in every tongue:</span>
            {LANG_TICKER.slice(0, 6).map((l) => (
              <span
                key={l.word}
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5"
                style={{ background: `${P.goldSoft}88`, color: P.ink, border: `1px solid ${P.gold}44` }}
                title={l.lang}
              >
                <span className="font-display">{l.word}</span>
              </span>
            ))}
          </div>
        </div>

        {/* ─── Right column: Rajasthani arch photo + memorial card overlay ───── */}
        <div className="relative">
          {/* Arch-shaped photo frame */}
          <div
            className="relative aspect-[4/5] w-full max-w-md mx-auto overflow-hidden"
            style={{
              // Ogee arch mask — the top is a rounded pointed arch,
              // reminiscent of Mughal / Rajput palace doorways.
              WebkitMaskImage:
                "radial-gradient(ellipse 100% 55% at 50% 55%, #000 60%, transparent 62%), linear-gradient(#000, #000)",
              maskImage:
                "radial-gradient(ellipse 100% 55% at 50% 55%, #000 60%, transparent 62%), linear-gradient(#000, #000)",
              // Faux arch: clip the top with an SVG path via CSS clip-path.
              clipPath:
                "path('M 12 200 Q 12 12 200 12 Q 388 12 388 200 L 388 500 L 12 500 Z')",
              boxShadow: `0 8px 24px ${P.ink}22, inset 0 0 0 8px ${P.gold}44`,
              background: P.bgDeep,
            }}
            data-testid="ift-hero-arch"
          >
            <img
              src={IFT_HERO_ARCH_URL}
              alt="Rajasthani palace arch"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              loading="lazy"
            />
            {/* Warm overlay to help text pop */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{ background: `linear-gradient(180deg, transparent 40%, ${P.ink}55 100%)` }}
            />
          </div>

          {/* Floating memorial card */}
          <div
            className="absolute left-2 sm:left-6 bottom-4 sm:bottom-8 rounded-xl bg-white shadow-xl border p-3 flex items-center gap-3 w-[240px] sm:w-[280px]"
            style={{ borderColor: `${P.gold}66` }}
            data-testid="ift-hero-memorial-card"
          >
            <img
              src={IFT_HERO_PORTRAIT_URL}
              alt=""
              className="w-14 h-14 rounded-full object-cover flex-none border-2"
              style={{ borderColor: P.gold }}
              onError={(e) => { e.currentTarget.style.display = "none"; }}
              loading="lazy"
            />
            <div className="min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.gold }}>In loving memory</div>
              <div className="font-display text-base leading-tight truncate" style={{ color: P.ink }}>Dadi Kamala</div>
              <div className="text-[11px] italic" style={{ color: P.saffron, fontFamily: "'Playfair Display', Georgia, serif" }}>
                1931 – 2019 · Fourth generation
              </div>
            </div>
          </div>

          {/* Corner ornament — a small mandala accent */}
          <div
            className="absolute -top-4 -right-4 w-16 h-16 opacity-70 hidden sm:block"
            aria-hidden
            style={{ color: P.gold }}
          >
            <svg viewBox="0 0 100 100" fill="currentColor">
              <circle cx="50" cy="50" r="4" />
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * Math.PI * 2) / 12;
                const x = 50 + Math.cos(a) * 28;
                const y = 50 + Math.sin(a) * 28;
                return <circle key={i} cx={x} cy={y} r="3" />;
              })}
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * Math.PI * 2) / 12 + Math.PI / 12;
                const x = 50 + Math.cos(a) * 16;
                const y = 50 + Math.sin(a) * 16;
                return <circle key={i} cx={x} cy={y} r="2" opacity="0.7" />;
              })}
            </svg>
          </div>
        </div>
      </div>
    </section>
  );
}


// ── Section: Multi-language "family" ticker ───────────────────────

function LanguageTicker() {
  const line = [...LANG_TICKER, ...LANG_TICKER]; // duplicate for seamless marquee
  return (
    <section className="border-b overflow-hidden"
             style={{ borderColor: `${P.gold}55`, background: P.ivory }}
             data-testid="ift-lang-ticker">
      <div className="flex whitespace-nowrap"
           style={{ animation: "ift-scroll 45s linear infinite" }}>
        {line.map((w, i) => (
          <div key={i} className="inline-flex items-baseline gap-2 px-6 py-2 flex-shrink-0">
            <span className="font-display italic text-lg sm:text-xl" style={{ color: P.ink }}>{w.word}</span>
            <span className="text-[9px] font-mono uppercase tracking-wider" style={{ color: P.gold }}>{w.lang}</span>
            <span className="mx-1" style={{ color: `${P.gold}88` }}>✦</span>
          </div>
        ))}
      </div>
      <style>{`
        @keyframes ift-scroll {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </section>
  );
}


// ── Section: Six-chapter manifesto ─────────────────────────────────

function Manifesto() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-manifesto">
      <Kicker roman="✦">A manifesto — Six chapters</Kicker>
      <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
        Six pillars of an Indian family archive.
      </h2>
      <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
        Not a social network. Not a database. A bound archive for every branch of your family — searchable, sharable, respectful.
      </p>
      <Ornament />
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {CHAPTERS.map((c) => (
          <div
            key={c.i}
            className="p-6 rounded-lg border-2 relative overflow-hidden"
            style={{ borderColor: `${P.gold}55`, background: P.ivory }}
            data-testid={`ift-chapter-${c.i}`}
          >
            <div className="absolute top-4 right-5 font-display italic text-4xl opacity-30" style={{ color: P.gold }}>
              {c.i}
            </div>
            <div className="w-10 h-10 rounded-full flex items-center justify-center mb-3"
                 style={{ background: `${P.saffron}22`, color: P.saffron }}>
              <c.Icon className="w-5 h-5" />
            </div>
            <h3 className="font-display text-2xl mb-2" style={{ color: P.ink }}>{c.title}</h3>
            <p className="text-sm leading-relaxed" style={{ color: P.inkSoft }}>{c.body}</p>
          </div>
        ))}
      </div>
    </section>
  );
}


// ── Section: Featured trees (real data + curated placeholders) ────

function FeaturedTrees() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-featured-trees">
      <Kicker roman="I">Featured Indian family trees</Kicker>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mt-2">
        <h2 className="font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
          A tree you can touch.
        </h2>
        <Link
          to="/family-trees"
          className="inline-flex items-center gap-1 text-sm font-mono uppercase tracking-widest hover:underline"
          style={{ color: P.saffron }}
          data-testid="ift-see-all-trees"
        >
          Explore every tree <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <Ornament char="⁂" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {FEATURED_TREES.map((t, i) => {
          const isReal = !!t.slug;
          const to = isReal ? `/family-trees/${t.slug}` : "/family-trees";
          return (
            <Link
              key={i}
              to={to}
              className="group block p-5 rounded-lg border-2 hover:shadow-lg transition-shadow"
              style={{ borderColor: `${P.gold}44`, background: P.ivory }}
              data-testid={`ift-tree-${(t.slug || t.name).toLowerCase().replace(/\s+/g, "-")}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <span className="font-display italic text-3xl" style={{ color: P.saffron }}>{t.motif}</span>
                {isReal ? (
                  <span className="ml-auto text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${P.tulsi}22`, color: P.tulsi }}>
                    Live archive
                  </span>
                ) : (
                  <span className="ml-auto text-[9px] font-mono uppercase tracking-widest px-2 py-0.5 rounded-full" style={{ background: `${P.gold}22`, color: P.inkSoft }}>
                    Coming soon
                  </span>
                )}
              </div>
              <h3 className="font-display text-2xl group-hover:underline" style={{ color: P.ink }}>{t.name}</h3>
              <div className="flex items-center gap-1 text-xs mt-1" style={{ color: P.inkSoft }}>
                <MapPin className="w-3 h-3" /> {t.region}
              </div>
              <p className="mt-3 text-sm leading-relaxed italic" style={{ color: P.inkSoft }}>
                "{t.tagline}"
              </p>
            </Link>
          );
        })}
      </div>
    </section>
  );
}


// ── Section: Discover your ancestral village ──────────────────────

function VillagePicker() {
  const [country, setCountry] = useState("India");
  const [stateSel, setStateSel] = useState("");
  const [village, setVillage] = useState("");

  return (
    <section className="py-20" style={{ background: P.bgDeep }} data-testid="ift-village-picker">
      <div className="max-w-6xl mx-auto px-4">
        <Kicker roman="II">Discover your ancestral village</Kicker>
        <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
          Where does your family begin?
        </h2>
        <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
          Start from the country, drill down to the state, then the district, then the village. Overlay migration routes on top.
        </p>
        <Ornament />
        <div className="grid gap-3 sm:grid-cols-4 max-w-4xl">
          <select value={country} onChange={(e) => setCountry(e.target.value)}
            className="rounded-lg px-3 py-3 border-2 bg-white text-sm font-medium"
            style={{ borderColor: `${P.gold}66`, color: P.ink }}
            data-testid="ift-village-country">
            <option value="India">India</option>
            <option value="Sri Lanka">Sri Lanka</option>
            <option value="Nepal">Nepal</option>
            <option value="Bangladesh">Bangladesh</option>
            <option value="Pakistan">Pakistan</option>
            <option value="Diaspora — UK">Diaspora — UK</option>
            <option value="Diaspora — US">Diaspora — US</option>
            <option value="Diaspora — Canada">Diaspora — Canada</option>
            <option value="Diaspora — Gulf">Diaspora — Gulf</option>
          </select>
          <select value={stateSel} onChange={(e) => setStateSel(e.target.value)}
            className="rounded-lg px-3 py-3 border-2 bg-white text-sm font-medium"
            style={{ borderColor: `${P.gold}66`, color: P.ink }}
            data-testid="ift-village-state">
            <option value="">State / Region…</option>
            {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={village} onChange={(e) => setVillage(e.target.value)} placeholder="Village or district name"
            className="rounded-lg px-3 py-3 border-2 bg-white text-sm"
            style={{ borderColor: `${P.gold}66`, color: P.ink }}
            data-testid="ift-village-input" />
          <Link
            to={`/explore-india${stateSel ? `?state=${encodeURIComponent(stateSel)}` : ""}${village ? `${stateSel ? "&" : "?"}village=${encodeURIComponent(village)}` : ""}`}
            className="inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-white font-semibold hover:opacity-90 transition-opacity"
            style={{ background: P.tulsi }}
            data-testid="ift-village-search"
          >
            <Search className="w-4 h-4" /> Discover
          </Link>
        </div>
        <div className="mt-4 flex items-center gap-2 flex-wrap text-xs font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>
          <span>Popular:</span>
          {["Kerala","Tamil Nadu","Karnataka","Punjab","Gujarat","West Bengal"].map((s) => (
            <Link key={s} to={`/state/${s.toLowerCase().replace(/\s+/g, "-")}`}
              className="hover:underline"
              style={{ color: P.saffron }}
              data-testid={`ift-village-chip-${s}`}>{s}</Link>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: Celebrate a life — memorial candles ──────────────────

function CelebrateLife() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-celebrate">
      <Kicker roman="III">Celebrate a life</Kicker>
      <div className="flex items-baseline justify-between flex-wrap gap-3 mt-2">
        <h2 className="font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
          A candle for those we remember.
        </h2>
        <Link to="/celebrate" className="inline-flex items-center gap-1 text-sm font-mono uppercase tracking-widest hover:underline" style={{ color: P.saffron }}>
          Memorial directory <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <Ornament char="✧" />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {MEMORIALS.map((m) => (
          <Link
            key={m.slug}
            to={`/celebrate/${m.slug}`}
            className="group block p-5 rounded-lg border-2 text-center hover:shadow-lg transition-shadow"
            style={{ borderColor: `${P.gold}44`, background: P.ivory }}
            data-testid={`ift-memorial-${m.slug}`}
          >
            <div className="w-14 h-14 mx-auto rounded-full flex items-center justify-center relative"
                 style={{ background: `radial-gradient(circle at 30% 30%, ${P.gold}, ${P.saffron})` }}>
              <Flame className="w-6 h-6 text-white" />
              <div className="absolute -top-1 -left-1 -right-1 -bottom-1 rounded-full animate-pulse"
                   style={{ boxShadow: `0 0 22px ${P.gold}66`, pointerEvents: "none" }} />
            </div>
            <h3 className="mt-4 font-display text-xl" style={{ color: P.ink }}>{m.name}</h3>
            <div className="mt-1 text-[11px] font-mono tracking-widest" style={{ color: P.gold }}>{m.years}</div>
            <p className="mt-2 text-xs italic" style={{ color: P.inkSoft }}>{m.note}</p>
          </Link>
        ))}
      </div>
      <div className="mt-6 text-center">
        <Link
          to="/obituaries/submit"
          className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 border-2 text-sm font-semibold hover:bg-white/30 transition-colors"
          style={{ borderColor: P.ink, color: P.ink }}
          data-testid="ift-submit-obituary"
        >
          <Flame className="w-4 h-4" /> Add a memorial · Submit an obituary
        </Link>
      </div>
    </section>
  );
}


// ── Section: Obituaries teaser ────────────────────────────────────
// Preview the most recent memorials — deep-links to /obituaries.

function ObituariesTeaser() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/obituaries`, { params: { limit: 6 } })
      .then((r) => setItems(r.data?.items || []))
      .catch(() => {});
  }, []);
  if (items.length === 0) return null;
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-obituaries">
      <Kicker roman="III·b">Obituaries · Remembering our own</Kicker>
      <div className="flex items-baseline justify-between gap-6 flex-wrap">
        <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
          The living remembers the departed.
        </h2>
        <Link to="/obituaries" className="text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1" style={{ color: P.saffron }} data-testid="ift-obituaries-all">
          All 485 obituaries <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
        A quiet archive of lives well lived — auto-curated from Malayalam, Tamil, Hindi and English obituaries across India and the diaspora. Add a candle. Share a memory. Preserve a name.
      </p>
      <Ornament />
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.slice(0, 6).map((m) => {
          const name = m.preferred_name || m.name_latin || m.full_name || "In loving memory";
          const locStr = m.location && typeof m.location === "object"
            ? [m.location.city, m.location.state, m.location.country].filter(Boolean).join(", ")
            : (m.location || "");
          const snippet = m.english_snippet || locStr || "";
          return (
            <Link key={m.slug || m.id} to={`/obituaries/${m.slug || m.id}`}
                  className="block rounded-lg border-2 p-5 hover:shadow-md transition-all"
                  style={{ background: P.ivory, borderColor: `${P.gold}55` }}
                  data-testid={`ift-obituary-${m.slug || m.id}`}>
              <div className="flex items-start gap-3">
                {m.portrait ? (
                  <img src={m.portrait} alt={name}
                       className="w-14 h-14 rounded-full object-cover border-2 flex-none"
                       style={{ borderColor: P.gold }} loading="lazy" />
                ) : (
                  <div className="w-14 h-14 rounded-full flex items-center justify-center border-2 flex-none"
                       style={{ borderColor: P.gold, background: P.goldSoft, color: P.saffronDeep }}>
                    <Flame className="w-6 h-6" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <div className="font-display text-base leading-tight line-clamp-2" style={{ color: P.ink }}>
                    {name}
                  </div>
                  {m.age_at_death && (
                    <div className="text-[10px] font-mono uppercase tracking-widest mt-0.5" style={{ color: P.saffron }}>
                      {m.age_at_death} years
                    </div>
                  )}
                  {snippet && (
                    <div className="text-xs mt-1 italic line-clamp-2" style={{ color: P.inkSoft }}>{snippet}</div>
                  )}
                </div>
              </div>
            </Link>
          );
        })}
      </div>
    </section>
  );
}


// ── Section: Famous Indian Families teaser ────────────────────────
// Ten dynasties that shaped a civilisation. Deep-links to /famous-families.

const FAMOUS_FAMILIES_TEASER = [
  { n: "01", slug: "nehru-gandhi", name: "Nehru–Gandhi",   origin: "Prayagraj",  kind: "Political",
    photo: "" },
  { n: "02", slug: "tata",         name: "Tata",           origin: "Navsari",    kind: "Business",
    photo: "" },
  { n: "03", slug: "birla",        name: "Birla",          origin: "Pilani",     kind: "Business",
    photo: "" },
  { n: "04", slug: "wadiyar",      name: "Wadiyar",        origin: "Mysuru",     kind: "Royal",
    photo: "" },
  { n: "05", slug: "scindia",      name: "Scindia",        origin: "Gwalior",    kind: "Royal · Political",
    photo: "" },
  { n: "06", slug: "holkar",       name: "Holkar",         origin: "Indore",     kind: "Royal",
    photo: "" },
  { n: "07", slug: "gaekwad",      name: "Gaekwad",        origin: "Vadodara",   kind: "Royal",
    photo: "https://images.pexels.com/photos/30320070/pexels-photo-30320070.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { n: "08", slug: "tagore",       name: "Tagore",         origin: "Kolkata",    kind: "Cultural",
    photo: "" },
];

function FamousFamiliesTeaser() {
  return (
    <section className="py-20" style={{ background: P.ivory, borderTop: `1px solid ${P.gold}55`, borderBottom: `1px solid ${P.gold}55` }} data-testid="ift-famous-families-teaser">
      <div className="max-w-6xl mx-auto px-4">
        <Kicker roman="III·c">India's Great Families</Kicker>
        <div className="flex items-baseline justify-between gap-6 flex-wrap">
          <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
            Lineages that shaped a civilisation.
          </h2>
          <Link to="/famous-families" className="text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1" style={{ color: P.saffron }} data-testid="ift-famous-all">
            Read all ten <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
          Ten families — royal, political, industrial, cultural — whose stories thread through India's modern history. Every profile links to a full page with lineage, patriarchs, matriarchs, and heritage sites.
        </p>
        <Ornament />
        <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
          {FAMOUS_FAMILIES_TEASER.map((f) => (
            <Link key={f.slug} to={`/famous-families#${f.slug}`}
                  className="group block rounded-lg overflow-hidden border-2 hover:shadow-md transition-all"
                  style={{ background: "#fff", borderColor: `${P.gold}55` }}
                  data-testid={`ift-famous-teaser-${f.slug}`}>
              <div className="aspect-[4/3] overflow-hidden" style={{ background: P.bgDeep }}>
                <img src={f.photo} alt={f.name}
                     className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                     loading="lazy" />
              </div>
              <div className="p-3">
                <div className="text-[10px] font-mono uppercase tracking-widest opacity-60">{f.n}</div>
                <div className="font-display text-lg mt-0.5" style={{ color: P.ink }}>{f.name}</div>
                <div className="text-[11px] font-mono uppercase tracking-wider mt-1" style={{ color: P.saffron }}>{f.origin} · {f.kind}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: Indian Heritage teaser ───────────────────────────────
// Ten pillars of Indian culture. Deep-links to /indian-heritage.

const HERITAGE_TEASER = [
  { icon: Users,    label: "Family values",      slug: "family-values",   count: "7 principles" },
  { icon: BookOpen, label: "Languages",          slug: "languages",       count: "22 scheduled" },
  { icon: MapPin,   label: "Village heritage",   slug: "village-heritage",count: "8 essentials" },
  { icon: Flame,    label: "Religions",          slug: "religions",       count: "10 faiths" },
  { icon: Sparkles, label: "Festivals",          slug: "festivals",       count: "16 major" },
  { icon: Feather,  label: "Arts & crafts",      slug: "arts-crafts",     count: "14 forms" },
  { icon: Landmark, label: "Classical dance",    slug: "dance",           count: "8 schools" },
  { icon: ScrollText, label: "Music",            slug: "music",           count: "5 systems" },
  { icon: Archive,  label: "Clothing",           slug: "clothing",        count: "11 traditions" },
  { icon: Compass,  label: "Regional cuisines",  slug: "cuisines",        count: "15 kitchens" },
];

function IndianHeritageTeaser() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-heritage-teaser">
      <Kicker roman="III·d">Indian Heritage</Kicker>
      <div className="flex items-baseline justify-between gap-6 flex-wrap">
        <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
          Ten pillars of a civilisation.
        </h2>
        <Link to="/indian-heritage" className="text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1" style={{ color: P.saffron }} data-testid="ift-heritage-all">
          Full essay <ArrowRight className="w-3 h-3" />
        </Link>
      </div>
      <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
        Languages, faiths, festivals, arts, dances, cuisines, textiles — every strand that binds an Indian family across generations, mapped as an evolving essay.
      </p>
      <Ornament />
      <div className="grid gap-2 grid-cols-2 sm:grid-cols-3 lg:grid-cols-5">
        {HERITAGE_TEASER.map((h) => (
          <Link key={h.slug} to={`/indian-heritage#${h.slug}`}
                className="group block rounded-lg border-2 p-4 hover:shadow-md transition-all"
                style={{ background: P.ivory, borderColor: `${P.gold}55` }}
                data-testid={`ift-heritage-teaser-${h.slug}`}>
            <h.icon className="w-5 h-5 mb-2" style={{ color: P.saffron }} />
            <div className="font-display text-base leading-tight" style={{ color: P.ink }}>{h.label}</div>
            <div className="text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: P.saffron }}>{h.count}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}


// ── Section: Explore India teaser ─────────────────────────────────
// Five regional bands. Deep-links to /explore-india.

const REGIONS_TEASER = [
  { key: "north", name: "North India", states: ["Delhi","Punjab","Haryana","Himachal Pradesh","J&K","Ladakh","Uttar Pradesh","Uttarakhand","Rajasthan"] },
  { key: "west",  name: "West India",  states: ["Gujarat","Maharashtra","Goa","Madhya Pradesh","Chhattisgarh"] },
  { key: "south", name: "South India", states: ["Kerala","Tamil Nadu","Karnataka","Andhra Pradesh","Telangana","Puducherry"] },
  { key: "east",  name: "East India",  states: ["West Bengal","Odisha","Bihar","Jharkhand"] },
  { key: "ne",    name: "Northeast",   states: ["Assam","Meghalaya","Tripura","Manipur","Mizoram","Nagaland","Arunachal","Sikkim"] },
];

function ExploreIndiaTeaser() {
  return (
    <section className="py-20" style={{ background: P.bgDeep }} data-testid="ift-explore-teaser">
      <div className="max-w-6xl mx-auto px-4">
        <Kicker roman="III·e">Explore India</Kicker>
        <div className="flex items-baseline justify-between gap-6 flex-wrap">
          <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
            One country, many worlds.
          </h2>
          <Link to="/explore-india" className="text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1" style={{ color: P.saffron }} data-testid="ift-explore-all">
            Every state <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
        <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
          Every Indian state carries its own languages, cuisines and family lineages. Tap a region to open its state archive — travel, culture, community, records.
        </p>
        <Ornament />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {REGIONS_TEASER.map((r) => (
            <Link key={r.key} to="/explore-india"
                  className="group block rounded-lg border-2 p-5 hover:shadow-md transition-all"
                  style={{ background: P.ivory, borderColor: `${P.gold}55` }}
                  data-testid={`ift-explore-teaser-${r.key}`}>
              <div className="flex items-baseline justify-between gap-2">
                <div className="font-display text-2xl" style={{ color: P.ink }}>{r.name}</div>
                <MapPin className="w-4 h-4 opacity-40" style={{ color: P.saffron }} />
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest mt-1" style={{ color: P.saffron }}>
                {r.states.length} states
              </div>
              <div className="text-xs mt-3 italic line-clamp-2" style={{ color: P.inkSoft }}>
                {r.states.join(" · ")}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: Contact & About ──────────────────────────────────────
// Closing "About + Contact" band. Deep-links to /about + writes to mailto.

function ContactBand() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-contact">
      <div className="grid gap-8 md:grid-cols-2 items-center">
        <div>
          <Kicker roman="IX">About &amp; Contact</Kicker>
          <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
            A quiet library, built with care.
          </h2>
          <p className="mt-4 text-base leading-relaxed" style={{ color: P.inkSoft }}>
            IndianFamilyTree.com is a heritage archive from the Social Pulse network — dedicated to helping Indian families document lineage, honour ancestors and connect across generations. We do not sell your data. We do not clutter your archive with ads. Every rupee of a paid membership supports the next chapter of a family's story.
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            <Link to="/about" className="rounded-full px-6 py-3 text-sm font-semibold text-white inline-flex items-center gap-2"
                  style={{ background: P.saffron }} data-testid="ift-contact-about-cta">
              Read the full story <ArrowRight className="w-4 h-4" />
            </Link>
            <a href="mailto:hello@indianfamilytree.com"
               className="rounded-full px-6 py-3 text-sm font-semibold border-2 inline-flex items-center gap-2"
               style={{ borderColor: P.saffron, color: P.saffron }}
               data-testid="ift-contact-email">
              Write to us
            </a>
          </div>
        </div>
        <div className="rounded-lg border-2 p-6 sm:p-8" style={{ background: P.ivory, borderColor: `${P.gold}55` }}>
          <div className="text-[10px] font-mono uppercase tracking-widest mb-3" style={{ color: P.saffron }}>Reach the archive</div>
          <dl className="space-y-4 text-sm" style={{ color: P.ink }}>
            <div>
              <dt className="text-[10px] font-mono uppercase tracking-widest opacity-60">Email</dt>
              <dd><a href="mailto:hello@indianfamilytree.com" className="hover:underline" style={{ color: P.saffronDeep }}>hello@indianfamilytree.com</a></dd>
            </div>
            <div>
              <dt className="text-[10px] font-mono uppercase tracking-widest opacity-60">General enquiries</dt>
              <dd><a href="mailto:info@indianfamilytree.com" className="hover:underline" style={{ color: P.saffronDeep }}>info@indianfamilytree.com</a></dd>
            </div>
            <div>
              <dt className="text-[10px] font-mono uppercase tracking-widest opacity-60">Parent network</dt>
              <dd><a href="https://socialpulse.com" target="_blank" rel="noreferrer" className="hover:underline" style={{ color: P.saffronDeep }}>Social Pulse</a></dd>
            </div>
            <div>
              <dt className="text-[10px] font-mono uppercase tracking-widest opacity-60">Sister archives</dt>
              <dd className="opacity-90">
                <a href="https://indias.com" target="_blank" rel="noreferrer" className="hover:underline">Indias.com</a> &nbsp;·&nbsp;
                <a href="https://kerala.com" target="_blank" rel="noreferrer" className="hover:underline">Kerala.com</a> &nbsp;·&nbsp;
                <a href="https://myfamilytree.care" target="_blank" rel="noreferrer" className="hover:underline">MyFamilyTree.care</a>
              </dd>
            </div>
          </dl>
        </div>
      </div>
    </section>
  );
}


// ── Section: Featured collections ─────────────────────────────────

function Collections() {
  return (
    <section className="py-20" style={{ background: P.bgDeep }} data-testid="ift-collections">
      <div className="max-w-6xl mx-auto px-4">
        <Kicker roman="IV">Featured collections</Kicker>
        <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
          The quieter corners of the archive.
        </h2>
        <Ornament char="✦" />
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          {COLLECTIONS.map((c) => (
            <Link
              key={c.slug}
              to={`/indian-heritage#${c.slug}`}
              className="group block p-4 rounded-lg text-left hover:shadow-md transition-shadow"
              style={{ background: P.ivory, borderLeft: `4px solid ${P.saffron}` }}
              data-testid={`ift-collection-${c.slug}`}
            >
              <c.Icon className="w-6 h-6 mb-2" style={{ color: P.saffron }} />
              <div className="font-display text-lg leading-tight" style={{ color: P.ink }}>{c.name}</div>
              <div className="text-xs mt-1 italic" style={{ color: P.inkSoft }}>{c.hint}</div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}


// ── Section: Migration timeline ───────────────────────────────────

function MigrationTimeline() {
  return (
    <section className="max-w-6xl mx-auto px-4 py-20" data-testid="ift-migration">
      <Kicker roman="V">Migration stories</Kicker>
      <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
        One family, many shores.
      </h2>
      <p className="mt-3 max-w-2xl text-base" style={{ color: P.inkSoft }}>
        Every Indian family carries the memory of a village they've left, and a city they've become. Two views on a single story — a canonical timeline, and the specific corridors that repeat again and again in the archive.
      </p>
      <Ornament char="∽" />
      <div className="grid gap-10 lg:grid-cols-2">
        {/* Canonical journey */}
        <div className="relative pl-6 sm:pl-8">
          <div className="absolute left-2 top-2 bottom-2 w-px" style={{ background: `linear-gradient(180deg, ${P.gold} 0%, ${P.gold}55 100%)` }} />
          <div className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: P.gold }}>
            A canonical journey
          </div>
          <div className="space-y-6">
            {MIGRATION_STOPS.map((s, i) => (
              <div key={i} className="relative">
                <div className="absolute -left-6 sm:-left-8 top-1 w-4 h-4 rounded-full flex items-center justify-center"
                     style={{ background: P.ivory, border: `2px solid ${P.saffron}` }}>
                  <span className="w-1.5 h-1.5 rounded-full" style={{ background: P.saffron }} />
                </div>
                <div className="flex items-baseline gap-3 flex-wrap">
                  <span className="font-mono text-[11px] tracking-widest uppercase" style={{ color: P.gold }}>{s.when}</span>
                  <h3 className="font-display text-xl sm:text-2xl" style={{ color: P.ink }}>{s.where}</h3>
                </div>
                <p className="mt-1 text-sm italic" style={{ color: P.inkSoft }}>{s.note}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Specific diaspora corridors */}
        <div className="rounded-lg border-2 p-5 sm:p-6"
             style={{ borderColor: `${P.gold}55`, background: P.ivory }}
             data-testid="ift-migration-routes">
          <div className="text-[10px] font-mono uppercase tracking-widest mb-4" style={{ color: P.gold }}>
            Corridors that repeat
          </div>
          <ul className="space-y-3">
            {MIGRATION_ROUTES.map((r, i) => (
              <li key={i}
                  className="flex flex-wrap items-baseline gap-x-3 gap-y-1 border-b pb-2 last:border-0 last:pb-0"
                  style={{ borderColor: `${P.gold}22` }}
                  data-testid={`ift-migration-route-${i}`}>
                <span className="font-display italic text-lg" style={{ color: P.ink }}>{r.from}</span>
                <span style={{ color: P.saffron }}>→</span>
                <span className="font-display italic text-lg" style={{ color: P.saffronDeep }}>{r.to}</span>
                <span className="ml-auto text-[10px] font-mono uppercase tracking-widest" style={{ color: P.gold }}>{r.year}</span>
                <div className="basis-full text-xs" style={{ color: P.inkSoft }}>{r.region}</div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}


// ── Section: Historical records ───────────────────────────────────

function HistoricalRecords() {
  return (
    <section className="py-20" style={{ background: P.ivory, borderTop: `1px solid ${P.gold}55`, borderBottom: `1px solid ${P.gold}55` }} data-testid="ift-records">
      <div className="max-w-6xl mx-auto px-4 grid gap-8 lg:grid-cols-2 items-center">
        <div>
          <Kicker roman="VI">Historical records</Kicker>
          <h2 className="mt-2 font-display italic text-4xl sm:text-5xl" style={{ color: P.ink }}>
            Old registers, oral histories, hand-written manuscripts.
          </h2>
          <p className="mt-3 text-base" style={{ color: P.inkSoft }}>
            We're building a searchable index of Indian records — census data, temple archives, colonial gazettes, panchayat books, church registers, mosque records. Contribute a scan of a family document and help millions of others find their branch.
          </p>

          {/* 14 record types the archive helps you search. */}
          <div className="mt-5 flex flex-wrap gap-2" data-testid="ift-record-types">
            {RECORD_TYPES.map((r) => (
              <span key={r}
                    data-testid={`ift-record-type-${r.toLowerCase().replace(/\s+/g, "-")}`}
                    className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-mono uppercase tracking-widest border"
                    style={{ borderColor: `${P.gold}66`, background: P.bg, color: P.inkSoft }}>
                {r}
              </span>
            ))}
          </div>

          {/* Archive scale stats. */}
          <div className="mt-6 grid grid-cols-3 gap-3 max-w-md" data-testid="ift-record-stats">
            {[
              { n: "1857",  l: "Oldest census"  },
              { n: "42k+",  l: "Temple entries" },
              { n: "18",    l: "Languages"      },
            ].map((s) => (
              <div key={s.l}
                   className="rounded-md p-3 text-center border-2"
                   style={{ borderColor: `${P.gold}55`, background: P.ivory }}>
                <div className="font-display text-2xl" style={{ color: P.saffron }}>{s.n}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.inkSoft }}>{s.l}</div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            <Link
              to="/family-tree-guide"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white"
              style={{ background: P.tulsi }}
              data-testid="ift-records-contribute"
            >
              <BookOpen className="w-4 h-4" /> How to contribute a record
            </Link>
            <Link
              to="/indian-heritage"
              className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold border-2"
              style={{ borderColor: P.ink, color: P.ink }}
              data-testid="ift-records-browse"
            >
              <Archive className="w-4 h-4" /> Browse the archive
            </Link>
          </div>
        </div>
        {/* Decorative page mock */}
        <div className="relative rounded-md p-6 sm:p-8" style={{ background: P.bg, boxShadow: `0 6px 24px -8px ${P.ink}44, inset 0 0 0 1px ${P.gold}66` }}>
          <div className="text-[10px] font-mono uppercase tracking-widest text-center" style={{ color: P.gold }}>
            Panchayat register · Kanjirappally · fol. 47
          </div>
          <Ornament />
          <div className="space-y-2 text-sm font-serif italic" style={{ color: P.ink }}>
            <div className="grid grid-cols-3 gap-2">
              <div className="opacity-70">1872</div><div>Kunjachan V.</div><div>b. Poonjar</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="opacity-70">1898</div><div>Kunjachan V.</div><div>m. Rebecca</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="opacity-70">1904</div><div>Joseph K.V.</div><div>b. Kanjirappally</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="opacity-70">1934</div><div>Joseph K.V.</div><div>m. Mariamma</div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div className="opacity-70">1956</div><div>Jose Joseph O.</div><div>b. Poonjar Thekkekara</div>
            </div>
          </div>
          <Ornament char="✦" />
          <div className="text-[10px] text-center opacity-70 italic" style={{ color: P.inkSoft }}>
            translation from Malayalam · digitised 2024
          </div>
        </div>
      </div>
    </section>
  );
}


// ── Compressed home sections (Days 1–14 Foundation) ───────────────
//
// Per the 90-day build plan the IFT homepage now routes visitors into
// three core actions (Start a Tree · Add a Memorial · Find Ancestors)
// with the minimum content needed to make each meaningful. Long-form
// manifesto content moved to /about; every unused legacy section
// component above this comment remains in the file but is not
// rendered on / — it is reused in /about (see IftAboutPage.jsx).

const CORE_PLACES = [
  { slug: "kerala",       label: "Kerala",        note: "Meenachil · Malabar · Travancore" },
  { slug: "tamil-nadu",   label: "Tamil Nadu",    note: "Chettinad · Palakkad borderlands" },
  { slug: "karnataka",    label: "Karnataka",     note: "Mangalore · Kodava · Nawabi Deccan" },
  { slug: "punjab",       label: "Punjab",        note: "Doaba · Majha · Malwa" },
  { slug: "gujarat",      label: "Gujarat",       note: "Kutch · Kathiawar · Saurashtra" },
  { slug: "maharashtra",  label: "Maharashtra",   note: "Konkan · Marathwada · Vidarbha" },
];

function ContinueYourStory() {
  // Logged-out fallback (per user choice: "big Start Your Tree CTA only").
  // We deliberately do NOT probe /api/me here — an empty payload keeps
  // this section server-friendly and instantly rendered on mobile.
  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="ift-continue"
    >
      <Kicker roman="I">Continue your story</Kicker>
      <h2
        className="mt-2 font-display italic text-3xl sm:text-4xl lg:text-5xl leading-tight"
        style={{ color: P.ink }}
      >
        Every tree begins with one name.
      </h2>
      <div
        className="mt-6 rounded-2xl border-2 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5"
        style={{ borderColor: `${P.gold}66`, background: P.ivory }}
      >
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-mono uppercase tracking-widest" style={{ color: P.gold }}>
            Get started in 30 seconds
          </div>
          <div className="mt-1 font-display text-2xl sm:text-3xl" style={{ color: P.ink }}>
            Start with yourself, add your parents, invite a cousin.
          </div>
          <p className="mt-2 text-sm sm:text-base" style={{ color: P.inkSoft }}>
            No credit card. Free forever for personal family trees.
          </p>
        </div>
        <Link
          to="/family-trees/new"
          data-testid="ift-continue-start-cta"
          className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-semibold text-white shadow-md hover:shadow-lg active:scale-[0.98] transition-all whitespace-nowrap w-full sm:w-auto justify-center"
          style={{ background: P.saffron }}
        >
          <TreePine className="w-4 h-4" /> Start My Family Tree <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </section>
  );
}

function ExploreByPlace() {
  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="ift-explore-places"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Kicker roman="II">Explore by place</Kicker>
          <h2
            className="mt-2 font-display italic text-3xl sm:text-4xl lg:text-5xl leading-tight"
            style={{ color: P.ink }}
          >
            Trace ancestors to the village.
          </h2>
        </div>
        <Link
          to="/places/india"
          data-testid="ift-explore-places-all"
          className="text-xs sm:text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1 self-start sm:self-auto"
          style={{ color: P.saffron }}
        >
          Browse every state <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      <p className="mt-3 max-w-2xl text-sm sm:text-base" style={{ color: P.inkSoft }}>
        Families are grouped by ancestral state, district and village. Verified pilot places
        show real counts of trees, members and memorials. Kerala · Kottayam · Pala is the
        first fully-verified region.
      </p>
      <div className="mt-6 grid gap-3 sm:gap-4 grid-cols-2 sm:grid-cols-3">
        {CORE_PLACES.map((p) => (
          <Link
            key={p.slug}
            to={`/places/india/${p.slug}`}
            data-testid={`ift-place-${p.slug}`}
            className="group block rounded-xl border-2 p-4 sm:p-5 hover:shadow-lg transition-all active:scale-[0.99]"
            style={{ borderColor: `${P.gold}44`, background: P.ivory }}
          >
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 flex-none" style={{ color: P.saffron }} />
              <span className="font-display text-lg sm:text-xl group-hover:underline" style={{ color: P.ink }}>
                {p.label}
              </span>
            </div>
            <div className="mt-1 text-xs sm:text-sm" style={{ color: P.inkSoft }}>
              {p.note}
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}

function RecentlyRemembered() {
  const [items, setItems] = useState([]);
  const [anniversaries, setAnniversaries] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, a] = await Promise.all([
          axios.get(`${API}/api/obituaries`, { params: { limit: 4 } }).catch(() => ({ data: {} })),
          // "On this day" — memorials whose anniversary is today or in the
          // next 3 days. Empty on quiet days, which is fine.
          axios.get(`${API}/api/memorial-anniversaries`, { params: { window: 3, limit: 4 } }).catch(() => ({ data: {} })),
        ]);
        if (!cancelled) {
          setItems(r.data?.items || []);
          setAnniversaries(a.data?.items || []);
        }
      } catch { /* leave empty */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="ift-recently-remembered"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Kicker roman="III">Recently remembered</Kicker>
          <h2
            className="mt-2 font-display italic text-3xl sm:text-4xl lg:text-5xl leading-tight"
            style={{ color: P.ink }}
          >
            A candle for those we have loved.
          </h2>
        </div>
        <Link
          to="/celebrate"
          data-testid="ift-remembered-all"
          className="text-xs sm:text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1 self-start sm:self-auto"
          style={{ color: P.saffron }}
        >
          Memorial directory <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {loading && (
        <div className="mt-6 h-24 rounded-lg animate-pulse" style={{ background: `${P.gold}22` }} />
      )}
      {!loading && items.length === 0 && (
        <div className="mt-6 rounded-lg border-2 p-6 text-center text-sm" style={{ borderColor: `${P.gold}44`, background: P.ivory, color: P.inkSoft }}>
          Be the first to add a memorial to the archive.{" "}
          <Link to="/celebrate/submit" className="underline font-semibold" style={{ color: P.saffron }} data-testid="ift-remembered-empty-cta">
            Create a memorial →
          </Link>
        </div>
      )}
      {/* Anniversary strip — surfaces when it's the 1st/5th/10th (etc.)
          anniversary of anyone in the archive. Silently hides when the
          calendar is quiet, so ordinary days stay clean. */}
      {!loading && anniversaries.length > 0 && (
        <div
          className="mt-4 sm:mt-6 rounded-2xl border-2 p-4 sm:p-5"
          style={{ borderColor: `${P.saffron}55`, background: `${P.saffron}0A` }}
          data-testid="ift-anniversaries"
        >
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest" style={{ color: P.saffron }}>
            <Flame className="w-3.5 h-3.5" /> On this day
          </div>
          <div className="mt-2 flex flex-wrap gap-3">
            {anniversaries.map((a) => {
              const nm = a.preferred_name || a.full_name || "In memory";
              const yearsAgo = a.years_ago;
              const ordinal = yearsAgo === 1 ? "1st" : yearsAgo === 2 ? "2nd" : yearsAgo === 3 ? "3rd" : `${yearsAgo}th`;
              return (
                <Link
                  key={a.slug || a.id}
                  to={`/memorial/${a.slug || a.id}`}
                  data-testid={`ift-anniv-${a.slug || a.id}`}
                  className="inline-flex items-center gap-2 rounded-full pl-1 pr-3 py-1 bg-white/80 hover:bg-white border transition-shadow hover:shadow-md"
                  style={{ borderColor: `${P.gold}66` }}
                >
                  {a.portrait
                    ? <img src={a.portrait} alt="" className="w-6 h-6 rounded-full object-cover" loading="lazy" />
                    : <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: `${P.gold}33`, color: P.saffronDeep, fontSize: 10 }}><Flame className="w-3 h-3" /></span>}
                  <span className="text-xs sm:text-sm font-semibold" style={{ color: P.ink }}>{nm}</span>
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded-full font-mono uppercase tracking-wider"
                    style={{
                      background: a.is_milestone ? P.saffron : `${P.gold}33`,
                      color: a.is_milestone ? "#fff" : P.saffronDeep,
                    }}
                  >
                    {ordinal} anniversary
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="mt-6 grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {items.slice(0, 4).map((m) => {
            const name = m.preferred_name || m.name_latin || m.full_name || "In loving memory";
            const locStr = m.location && typeof m.location === "object"
              ? [m.location.city, m.location.state].filter(Boolean).join(", ")
              : (m.location || "");
            return (
              <Link
                key={m.slug || m.id}
                to={`/obituaries/${m.slug || m.id}`}
                data-testid={`ift-remembered-${m.slug || m.id}`}
                className="group block rounded-xl border-2 p-4 hover:shadow-md transition-all active:scale-[0.99]"
                style={{ borderColor: `${P.gold}44`, background: P.ivory }}
              >
                <div className="flex items-start gap-3">
                  {m.portrait ? (
                    <img src={m.portrait} alt={name}
                         className="w-12 h-12 rounded-full object-cover border-2 flex-none"
                         style={{ borderColor: P.gold }} loading="lazy" />
                  ) : (
                    <div className="w-12 h-12 rounded-full flex items-center justify-center border-2 flex-none"
                         style={{ borderColor: P.gold, background: P.goldSoft, color: P.saffronDeep }}>
                      <Flame className="w-5 h-5" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-display text-base leading-tight line-clamp-2 group-hover:underline" style={{ color: P.ink }}>
                      {name}
                    </div>
                    {(m.age_at_death || locStr) && (
                      <div className="text-[11px] mt-1 italic line-clamp-2" style={{ color: P.inkSoft }}>
                        {[m.age_at_death && `${m.age_at_death} yrs`, locStr].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}

function FamilyHeritageNews() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Prefer the curated genealogy stream when available; fall back
        // to the top /things-to-know slice so the section never renders
        // empty on a fresh preview pod.
        let list = [];
        try {
          const r1 = await axios.get(`${API}/api/things-to-know`, { params: { limit: 4, section: "heritage" } });
          list = r1.data?.items || [];
        } catch { /* fall through */ }
        if (!cancelled) setItems(list.slice(0, 4));
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);
  if (!loading && items.length === 0) return null;
  return (
    <section
      className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14"
      data-testid="ift-heritage-news"
    >
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <Kicker roman="IV">Family &amp; Heritage News</Kicker>
          <h2
            className="mt-2 font-display italic text-3xl sm:text-4xl lg:text-5xl leading-tight"
            style={{ color: P.ink }}
          >
            Stories that shape our lineage.
          </h2>
        </div>
        <Link
          to="/things-to-know"
          data-testid="ift-heritage-news-all"
          className="text-xs sm:text-sm font-mono uppercase tracking-widest hover:underline inline-flex items-center gap-1 self-start sm:self-auto"
          style={{ color: P.saffron }}
        >
          Full archive <ArrowRight className="w-3.5 h-3.5" />
        </Link>
      </div>
      {loading ? (
        <div className="mt-6 h-24 rounded-lg animate-pulse" style={{ background: `${P.gold}22` }} />
      ) : (
        <div className="mt-6 grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((n, idx) => (
            <Link
              key={n.slug || n.id || idx}
              to={n.slug ? `/things-to-know/${n.slug}` : "/things-to-know"}
              data-testid={`ift-heritage-news-${idx}`}
              className="group block rounded-xl border-2 overflow-hidden hover:shadow-md transition-all active:scale-[0.99]"
              style={{ borderColor: `${P.gold}44`, background: P.ivory }}
            >
              {n.hero_image && (
                <img
                  src={n.hero_image}
                  alt={n.title || ""}
                  className="w-full h-32 object-cover"
                  loading="lazy"
                />
              )}
              <div className="p-4">
                <div className="text-[10px] font-mono uppercase tracking-widest" style={{ color: P.gold }}>
                  {n.section || "Heritage"}
                </div>
                <div className="mt-1 font-display text-base leading-snug line-clamp-3 group-hover:underline" style={{ color: P.ink }}>
                  {n.title || n.headline || "Untitled"}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}


// ── Main page ─────────────────────────────────────────────────────

export default function IndianFamilyTreeHome() {
  const [stats, setStats] = useState({ trees: 0, members: 0, states: 0 });

  useEffect(() => {
    document.title = "Indian Family Tree — Every family, every generation";
    let cancelled = false;
    (async () => {
      try {
        // Omit the X-Site-Source header so this stays a "simple" GET
        // (no custom headers → no CORS preflight). Backend already
        // derives child-site from the Origin header on production.
        const res = await axios.get(`${API}/api/family-trees/discover?limit=1&country=India`);
        if (cancelled) return;
        const list = res.data.items || res.data.trees || [];
        setStats({
          trees:   res.data.total_trees   ?? list.length,
          members: res.data.total_members ?? list.reduce((a, t) => a + (t.member_count || 0), 0),
          states:  res.data.total_states  ?? INDIAN_STATES.length,
        });
      } catch { /* API unavailable — page still renders */ }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div style={{ background: P.bg, color: P.ink, minHeight: "100vh" }} data-testid="ift-home">
      <SeoHead
        section="family_tree"
        title="IndianFamilyTree.com — Preserving Heritage, Honoring Roots"
        description="A culturally-grounded platform for Indian families to document genealogy, plan reunions, and honour loved ones with free digital memorials. Every family has a story worth preserving."
        url="https://indianfamilytree.com/"
        image={
          typeof window !== "undefined"
            ? `${window.location.origin}/api/og/card/ift.png`
            : "https://indianfamilytree.com/api/og/card/ift.png"
        }
      />

      <TaglineStrip />
      <LanguageTicker />
      <Hero />

      {/* ── Compressed home: Hero + 4 focused sections ─────────────
          Per /app/memory/BUILD_PLAN_90DAY.md the homepage now routes
          visitors into the three core actions without the long-form
          manifesto. Legacy sections (Manifesto, Featured Trees, Migration
          Timeline, Historical Records etc.) moved to /about — the section
          components stay in this file so /about can compose them.       */}
      <ContinueYourStory />
      <ExploreByPlace />
      <RecentlyRemembered />
      <FamilyHeritageNews />

      {/* Cousin Discovery — kept because it drives the "one person, one
          identity" flow that the 90-day plan calls the highest-leverage
          repeat-visit hook. Compact single-strip layout, not a section. */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8"
               data-testid="ift-cousin-discovery">
        <CousinDiscoveryFeed palette={P} siteScope="indianfamilytree" />
      </section>

      {/* Trust strip — live stats (dynamic, no hardcoded caches). */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-10" data-testid="ift-stats">
        <div className="grid gap-3 sm:gap-4 grid-cols-3 items-center text-center rounded-2xl border-2 p-5 sm:p-6"
             style={{ borderColor: `${P.gold}44`, background: P.ivory }}>
          <div>
            <div className="font-display text-2xl sm:text-3xl lg:text-4xl" style={{ color: P.saffron }}>{stats.trees || "—"}</div>
            <div className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: P.inkSoft }}>Family trees</div>
          </div>
          <div>
            <div className="font-display text-2xl sm:text-3xl lg:text-4xl" style={{ color: P.tulsi }}>{stats.members || "—"}</div>
            <div className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: P.inkSoft }}>Ancestors documented</div>
          </div>
          <div>
            <div className="font-display text-2xl sm:text-3xl lg:text-4xl" style={{ color: P.gold }}>{stats.states || INDIAN_STATES.length}</div>
            <div className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest mt-1" style={{ color: P.inkSoft }}>Indian states covered</div>
          </div>
        </div>
      </section>

      {/* Closing ornament — routes to /about for the manifesto + full history. */}
      <div className="py-10 sm:py-14 text-center px-4">
        <div className="text-3xl sm:text-4xl" style={{ color: P.gold }}>❦</div>
        <p className="mt-3 text-sm italic max-w-md mx-auto" style={{ color: P.inkSoft }}>
          Every family, every generation — bound in one archive.
        </p>
        <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
          <Link
            to="/about"
            className="inline-flex items-center gap-1 rounded-full border-2 px-4 py-2 text-xs sm:text-sm font-mono uppercase tracking-widest hover:bg-white/40 transition-colors"
            style={{ borderColor: P.ink, color: P.ink }}
            data-testid="ift-about-link"
          >
            About the archive <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <Link
            to="/pricing"
            className="inline-flex items-center gap-1 rounded-full px-4 py-2 text-xs sm:text-sm font-semibold text-white transition-opacity hover:opacity-90"
            style={{ background: P.tulsi }}
            data-testid="ift-pricing-link"
          >
            See membership plans
          </Link>
        </div>
      </div>
    </div>
  );
}
